import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Row, Form, Button, Spinner } from "react-bootstrap";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import imageCompression from "browser-image-compression";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

const UPLOAD_BASE = (process.env.REACT_APP_API || "http://localhost:3002/api")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");
const PHOTO_COMPRESS_OPTS = { maxSizeMB: 0.8, maxWidthOrHeight: 1280, useWebWorker: true, fileType: "image/jpeg" };
const MAX_PHOTO_UPLOAD_BYTES = 950 * 1024;

const resolveUploadUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("blob:")) return path;
  return `${UPLOAD_BASE}${path.startsWith("/") ? path : `/${path}`}`;
};

// คำนำหน้าชื่อ → เพศ (เรียงจากยาวไปสั้นเพื่อให้จับคำที่เฉพาะเจาะจงก่อน)
const NAME_PREFIXES = [
  { prefix: "เด็กหญิง", gen: "f" },
  { prefix: "เด็กชาย", gen: "m" },
  { prefix: "นางสาว", gen: "f" },
  { prefix: "ด.ช.", gen: "m" },
  { prefix: "ด.ญ.", gen: "f" },
  { prefix: "น.ส.", gen: "f" },
  { prefix: "ดช.", gen: "m" },
  { prefix: "ดญ.", gen: "f" },
  { prefix: "นส.", gen: "f" },
  { prefix: "นาย", gen: "m" },
].sort((a, b) => b.prefix.length - a.prefix.length);

const normalizeStudentName = (value) => {
  for (const { prefix, gen } of NAME_PREFIXES) {
    if (value.startsWith(prefix)) {
      const rest = value.slice(prefix.length);
      const name = rest.replace(/^\s+/, "");
      let normalized;
      if (name.length > 0) normalized = `${prefix} ${name}`;
      else if (rest.length > 0) normalized = `${prefix} `;
      else normalized = prefix;
      return { value: normalized, gen };
    }
  }
  return { value, gen: null };
};

const EMPTY_FORM = {
  st_idnumber: "", st_name: "", st_arabicname: "", st_photo: "", st_gen: "", st_blood: "",
  st_idcard: "", st_birthdate: "", st_nationality: "", st_race: "", st_religion: "",
  st_childnumber: "", st_sibling: "", st_malesibling: "", st_femalesibling: "",
  st_homeID: "", st_oldaddress: "", st_oldSubDistrict: "", st_oldDistrict: "", st_oldProvince: "", st_oldZipcode: "",
  st_current: "", st_currentSubDistrict: "", st_currentDistrict: "", st_currentProvince: "", st_zipcode: "",
  st_fatherIdCard: "", st_fatherBirthdate: "", st_fatherName: "", st_fatherJob: "", st_fatherAnnualIncome: "",
  st_fatherAddress: "", st_fatherSubDistrict: "", st_fatherDistrict: "", st_fatherProvince: "", st_fatherZipcode: "", st_fatherPhone: "",
  st_motherIdCard: "", st_motherBirthdate: "", st_motherName: "", st_motherJob: "", st_motherAnnualIncome: "",
  st_motherAddress: "", st_motherSubDistrict: "", st_motherDistrict: "", st_motherProvince: "", st_motherZipcode: "", st_motherPhone: "",
  st_averageAnnualIncome: "", st_parentStatus: "", st_parent: "", st_parentRelation: "",
  st_parentIdCard: "", st_parentBirthdate: "", st_parentname: "", st_parentjob: "", st_parentannualincome: "",
  st_parentAddress: "", st_parentSubDistrict: "", st_parentDistrict: "", st_parentProvince: "", st_parentZipcode: "", st_parentPhone: "",
  st_educationsupporter: "", st_favoritesport: "", st_favoritesubject: "", st_futurestudy: "", st_futurejob: "",
};

export default function UpdateIslamicStudent() {
  const { studentID } = useParams();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const irId = searchParams.get("ir_id");
  const isAdvisorView = pathname.startsWith("/islamic/advisor/student-info/");
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = (lang === "th" ? th : jawi).updateStudent;

  const photoInputRef = useRef(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM, st_id: studentID });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`/api/getStudentInfo/${studentID}`)
      .then((response) => {
        const normalized = Object.fromEntries(
          Object.entries(response.data).map(([k, v]) => [k, v ?? ""])
        );
        setFormData((prev) => ({ ...prev, ...normalized }));
        setPhotoPreview(resolveUploadUrl(normalized.st_photo));
      })
      .catch(() => {
        Swal.fire({ icon: "error", title: t.loadErrorTitle, text: t.loadErrorText });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentID]);

  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "st_name") {
      const { value: normalized, gen } = normalizeStudentName(value);
      setFormData((prev) => ({ ...prev, st_name: normalized, ...(gen ? { st_gen: gen } : {}) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      Swal.fire({ icon: "warning", title: t.invalidFileTitle, text: t.imageOnlyText });
      return;
    }

    const applyPreparedPhoto = (preparedFile) => {
      if (preparedFile.size > MAX_PHOTO_UPLOAD_BYTES) {
        setPhotoFile(null);
        Swal.fire({ icon: "warning", title: t.photoLargeTitle, text: t.photoLargeText });
        return;
      }
      setPhotoFile(preparedFile);
      setPhotoPreview((prev) => {
        if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
        return URL.createObjectURL(preparedFile);
      });
    };

    setProcessingPhoto(true);
    try {
      const compressed = await imageCompression(file, PHOTO_COMPRESS_OPTS);
      const jpgFile = new File([compressed], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
      applyPreparedPhoto(jpgFile);
    } catch (error) {
      applyPreparedPhoto(file);
    } finally {
      setProcessingPhoto(false);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) return;
    setUploadingPhoto(true);
    const fd = new FormData();
    fd.append("studentPhoto", photoFile);
    try {
      const response = await axiosInstance.post(`/api/uploadStudentPhoto/${studentID}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const photoPath = response.data.photoPath || "";
      setFormData((prev) => ({ ...prev, st_photo: photoPath }));
      setPhotoFile(null);
      setPhotoPreview(resolveUploadUrl(photoPath));
      if (photoInputRef.current) photoInputRef.current.value = "";
      Swal.fire({ icon: "success", title: t.uploadOkTitle, timer: 1200, showConfirmButton: false });
    } catch (error) {
      Swal.fire({ icon: "error", title: t.uploadFailTitle, text: error.response?.data?.message || t.uploadFailText });
    } finally {
      setUploadingPhoto(false);
    }
  };

  // เลือกผู้ปกครอง (บิดา/มารดา/อื่นๆ) → คัดลอกข้อมูลตามที่เลือก
  const handleParentSelection = (parent) => {
    const d = { ...formData, st_parent: parent };
    if (parent === "dad") {
      d.st_parentRelation = t.guardianDad;
      d.st_parentIdCard = formData.st_fatherIdCard;
      d.st_parentBirthdate = formData.st_fatherBirthdate;
      d.st_parentname = formData.st_fatherName;
      d.st_parentjob = formData.st_fatherJob;
      d.st_parentannualincome = formData.st_fatherAnnualIncome;
      d.st_parentAddress = formData.st_fatherAddress;
      d.st_parentSubDistrict = formData.st_fatherSubDistrict;
      d.st_parentDistrict = formData.st_fatherDistrict;
      d.st_parentProvince = formData.st_fatherProvince;
      d.st_parentZipcode = formData.st_fatherZipcode;
      d.st_parentPhone = formData.st_fatherPhone;
      d.st_educationsupporter = formData.st_fatherName;
    } else if (parent === "mom") {
      d.st_parentRelation = t.guardianMom;
      d.st_parentIdCard = formData.st_motherIdCard;
      d.st_parentBirthdate = formData.st_motherBirthdate;
      d.st_parentname = formData.st_motherName;
      d.st_parentjob = formData.st_motherJob;
      d.st_parentannualincome = formData.st_motherAnnualIncome;
      d.st_parentAddress = formData.st_motherAddress;
      d.st_parentSubDistrict = formData.st_motherSubDistrict;
      d.st_parentDistrict = formData.st_motherDistrict;
      d.st_parentProvince = formData.st_motherProvince;
      d.st_parentZipcode = formData.st_motherZipcode;
      d.st_parentPhone = formData.st_motherPhone;
      d.st_educationsupporter = formData.st_motherName;
    } else {
      d.st_parentRelation = "";
      d.st_parentIdCard = "";
      d.st_parentBirthdate = "";
      d.st_parentname = "";
      d.st_parentjob = "";
      d.st_parentannualincome = "";
      d.st_parentAddress = "";
      d.st_parentSubDistrict = "";
      d.st_parentDistrict = "";
      d.st_parentProvince = "";
      d.st_parentZipcode = "";
      d.st_parentPhone = "";
      d.st_educationsupporter = "";
    }
    setFormData(d);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post("/api/updateStudentInfo", formData);
      Swal.fire({ icon: "success", title: t.saveSuccessTitle, text: t.saveSuccessText });
    } catch (error) {
      Swal.fire({ icon: "error", title: t.saveErrorTitle, text: error.response?.data?.message || t.saveErrorText });
    } finally {
      setLoading(false);
    }
  };

  const field = (name, label, { type = "text", as, rows, disabled } = {}) => (
    <Form.Group>
      <Form.Label className="fw-medium mb-1">{label}</Form.Label>
      <Form.Control
        type={type}
        as={as}
        rows={rows}
        name={name}
        value={formData[name] ?? ""}
        onChange={handleInputChange}
        disabled={disabled}
        className={name === "st_arabicname" ? "arabic-text" : undefined}
      />
    </Form.Group>
  );

  const backToRoom = isAdvisorView
    ? "/islamic/advisor/students"
    : irId ? `/islamic/manage-students/${irId}` : "/islamic/manage-rooms";

  return (
    <div className={isAdvisorView ? "main main-app p-3 p-lg-4" : undefined}>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item"><Link to="/dashboard/school-statistic">{t.breadHome}</Link></li>
            {!isAdvisorView && <li className="breadcrumb-item"><Link to="/islamic/manage-rooms">{t.breadRooms}</Link></li>}
            <li className="breadcrumb-item"><Link to={backToRoom}>{t.breadStudents}</Link></li>
            <li className="breadcrumb-item active">{t.breadCurrent}</li>
          </ol>
          <h4 className="main-title mb-0">{t.pageTitle}</h4>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        <Card className="card-one mb-3">
          <Card.Header><Card.Title as="h6" className="mb-0">{t.cardTitle}</Card.Title></Card.Header>
          <Card.Body>
            {/* รูปนักเรียน */}
            <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 mb-4">
              <div
                className="border bg-light d-flex align-items-center justify-content-center overflow-hidden"
                style={{ width: "min(100%, 160px)", aspectRatio: "3 / 4", borderRadius: 8 }}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt={formData.st_name || "student"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div className="text-center text-secondary px-3">
                    <i className="ri-user-line fs-1 d-block mb-1"></i>
                    <span className="fs-sm">{t.photoNone}</span>
                  </div>
                )}
              </div>
              <div className="flex-grow-1 text-center text-md-start">
                <div className="fw-semibold mb-1">{formData.st_name}</div>
                <div className="text-secondary fs-sm mb-3">{formData.st_idnumber}</div>
                <input ref={photoInputRef} type="file" accept="image/*" className="d-none" onChange={handlePhotoChange} disabled={processingPhoto || uploadingPhoto} />
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <Button type="button" variant="outline-primary" onClick={() => photoInputRef.current?.click()} disabled={processingPhoto || uploadingPhoto}>
                    <i className="ri-image-add-line me-1"></i>{t.choosePhoto}
                  </Button>
                  <Button type="button" variant="primary" onClick={handlePhotoUpload} disabled={!photoFile || processingPhoto || uploadingPhoto}>
                    {processingPhoto || uploadingPhoto ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="ri-upload-cloud-line me-1"></i>}
                    {processingPhoto ? t.preparingPhoto : t.uploadPhoto}
                  </Button>
                </div>
              </div>
            </div>

            {/* ข้อมูลส่วนตัว */}
            <h6 className="fw-semibold mb-3">{t.secPersonal}</h6>
            <Row className="g-3 mb-2">
              <Col md={3}>{field("st_idnumber", t.fIdnumber, { disabled: true })}</Col>
              <Col md={5}>{field("st_name", t.fName)}</Col>
              <Col md={4}>{field("st_arabicname", t.fArabicName)}</Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium mb-1">{t.fGender}</Form.Label>
                  <div>
                    <Form.Check inline type="radio" label={t.male} name="st_gen" value="m" checked={formData.st_gen === "m"} onChange={handleInputChange} />
                    <Form.Check inline type="radio" label={t.female} name="st_gen" value="f" checked={formData.st_gen === "f"} onChange={handleInputChange} />
                  </div>
                </Form.Group>
              </Col>
              <Col md={2}>{field("st_blood", t.fBlood)}</Col>
              <Col md={3}>{field("st_idcard", t.fIdcard)}</Col>
              <Col md={3}>{field("st_birthdate", t.fBirthdate)}</Col>
              <Col md={4}>{field("st_nationality", t.fNationality)}</Col>
              <Col md={4}>{field("st_race", t.fRace)}</Col>
              <Col md={4}>{field("st_religion", t.fReligion)}</Col>
            </Row>

            {/* ข้อมูลพี่น้อง */}
            <hr className="my-3" />
            <h6 className="fw-semibold mb-3">{t.secSibling}</h6>
            <Row className="g-3">
              <Col md={3}>{field("st_childnumber", t.fChildNumber, { type: "number" })}</Col>
              <Col md={3}>{field("st_sibling", t.fSiblingTotal, { type: "number" })}</Col>
              <Col md={3}>{field("st_malesibling", t.fSiblingMale, { type: "number" })}</Col>
              <Col md={3}>{field("st_femalesibling", t.fSiblingFemale, { type: "number" })}</Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ที่อยู่ */}
        <Card className="card-one mb-3">
          <Card.Body>
            <h6 className="fw-semibold mb-3">{t.secAddressOld}</h6>
            <Row className="g-3 mb-2">
              <Col md={4}>{field("st_homeID", t.fHomeId)}</Col>
              <Col md={8}>{field("st_oldaddress", t.fAddress, { as: "textarea", rows: 2 })}</Col>
              <Col md={3}>{field("st_oldSubDistrict", t.fSubDistrict)}</Col>
              <Col md={3}>{field("st_oldDistrict", t.fDistrict)}</Col>
              <Col md={3}>{field("st_oldProvince", t.fProvince)}</Col>
              <Col md={3}>{field("st_oldZipcode", t.fZipcode)}</Col>
            </Row>
            <hr className="my-3" />
            <h6 className="fw-semibold mb-3">{t.secAddressCurrent}</h6>
            <Row className="g-3">
              <Col md={12}>{field("st_current", t.fAddress, { as: "textarea", rows: 2 })}</Col>
              <Col md={3}>{field("st_currentSubDistrict", t.fSubDistrict)}</Col>
              <Col md={3}>{field("st_currentDistrict", t.fDistrict)}</Col>
              <Col md={3}>{field("st_currentProvince", t.fProvince)}</Col>
              <Col md={3}>{field("st_zipcode", t.fZipcode)}</Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ข้อมูลบิดา */}
        <Card className="card-one mb-3">
          <Card.Body>
            <h6 className="fw-semibold mb-3">{t.secFather}</h6>
            <Row className="g-3">
              <Col md={4}>{field("st_fatherName", t.fName2)}</Col>
              <Col md={4}>{field("st_fatherIdCard", t.fIdcard)}</Col>
              <Col md={4}>{field("st_fatherBirthdate", t.fBirthdate)}</Col>
              <Col md={4}>{field("st_fatherJob", t.fJob)}</Col>
              <Col md={4}>{field("st_fatherAnnualIncome", t.fIncomeYear, { type: "number" })}</Col>
              <Col md={4}>{field("st_fatherPhone", t.fPhone)}</Col>
              <Col md={12}>{field("st_fatherAddress", t.fAddress, { as: "textarea", rows: 2 })}</Col>
              <Col md={3}>{field("st_fatherSubDistrict", t.fSubDistrict)}</Col>
              <Col md={3}>{field("st_fatherDistrict", t.fDistrict)}</Col>
              <Col md={3}>{field("st_fatherProvince", t.fProvince)}</Col>
              <Col md={3}>{field("st_fatherZipcode", t.fZipcode)}</Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ข้อมูลมารดา */}
        <Card className="card-one mb-3">
          <Card.Body>
            <h6 className="fw-semibold mb-3">{t.secMother}</h6>
            <Row className="g-3">
              <Col md={4}>{field("st_motherName", t.fName2)}</Col>
              <Col md={4}>{field("st_motherIdCard", t.fIdcard)}</Col>
              <Col md={4}>{field("st_motherBirthdate", t.fBirthdate)}</Col>
              <Col md={4}>{field("st_motherJob", t.fJob)}</Col>
              <Col md={4}>{field("st_motherAnnualIncome", t.fIncomeYear, { type: "number" })}</Col>
              <Col md={4}>{field("st_motherPhone", t.fPhone)}</Col>
              <Col md={12}>{field("st_motherAddress", t.fAddress, { as: "textarea", rows: 2 })}</Col>
              <Col md={3}>{field("st_motherSubDistrict", t.fSubDistrict)}</Col>
              <Col md={3}>{field("st_motherDistrict", t.fDistrict)}</Col>
              <Col md={3}>{field("st_motherProvince", t.fProvince)}</Col>
              <Col md={3}>{field("st_motherZipcode", t.fZipcode)}</Col>
            </Row>
          </Card.Body>
        </Card>

        {/* สถานะและผู้ปกครอง */}
        <Card className="card-one mb-3">
          <Card.Body>
            <h6 className="fw-semibold mb-3">{t.secGuardian}</h6>
            <Row className="g-3 mb-2">
              <Col md={4}>{field("st_averageAnnualIncome", t.fAverageIncome, { type: "number" })}</Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-medium mb-1">{t.fParentStatus}</Form.Label>
                  <div>
                    <Form.Check inline type="radio" label={t.stTogether} name="st_parentStatus" value="togather" checked={formData.st_parentStatus === "togather"} onChange={handleInputChange} />
                    <Form.Check inline type="radio" label={t.stSeparated} name="st_parentStatus" value="separated" checked={formData.st_parentStatus === "separated"} onChange={handleInputChange} />
                    <Form.Check inline type="radio" label={t.stDivorced} name="st_parentStatus" value="divorce" checked={formData.st_parentStatus === "divorce"} onChange={handleInputChange} />
                  </div>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-medium mb-1">{t.fGuardianType}</Form.Label>
                  <div>
                    <Form.Check inline type="radio" label={t.guardianDad} name="st_parent" value="dad" checked={formData.st_parent === "dad"} onChange={() => handleParentSelection("dad")} />
                    <Form.Check inline type="radio" label={t.guardianMom} name="st_parent" value="mom" checked={formData.st_parent === "mom"} onChange={() => handleParentSelection("mom")} />
                    <Form.Check inline type="radio" label={t.guardianOther} name="st_parent" value="other" checked={formData.st_parent === "other"} onChange={() => handleParentSelection("other")} />
                  </div>
                </Form.Group>
              </Col>
              <Col md={4}>{field("st_parentRelation", t.fRelation)}</Col>
              <Col md={4}>{field("st_parentname", t.fName2)}</Col>
              <Col md={4}>{field("st_parentIdCard", t.fIdcard)}</Col>
              <Col md={4}>{field("st_parentBirthdate", t.fBirthdate)}</Col>
              <Col md={4}>{field("st_parentjob", t.fJob)}</Col>
              <Col md={4}>{field("st_parentannualincome", t.fIncomeYear, { type: "number" })}</Col>
              <Col md={4}>{field("st_parentPhone", t.fPhone)}</Col>
              <Col md={12}>{field("st_parentAddress", t.fAddress, { as: "textarea", rows: 2 })}</Col>
              <Col md={3}>{field("st_parentSubDistrict", t.fSubDistrict)}</Col>
              <Col md={3}>{field("st_parentDistrict", t.fDistrict)}</Col>
              <Col md={3}>{field("st_parentProvince", t.fProvince)}</Col>
              <Col md={3}>{field("st_parentZipcode", t.fZipcode)}</Col>
              <Col md={4}>{field("st_educationsupporter", t.fExpensePayer)}</Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ข้อมูลอื่นๆ */}
        <Card className="card-one mb-3">
          <Card.Body>
            <h6 className="fw-semibold mb-3">{t.secOther}</h6>
            <Row className="g-3">
              <Col md={3}>{field("st_favoritesport", t.fFavoriteSport)}</Col>
              <Col md={3}>{field("st_favoritesubject", t.fFavoriteSubject)}</Col>
              <Col md={3}>{field("st_futurestudy", t.fFutureStudy)}</Col>
              <Col md={3}>{field("st_futurejob", t.fFutureJob)}</Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-end gap-2 mb-4">
          <Button variant="secondary" as={Link} to={backToRoom}>{(lang === "th" ? th : jawi).common.cancel}</Button>
          <Button type="submit" variant="success"><i className="ri-save-line me-1"></i>{t.saveBtn}</Button>
        </div>
      </Form>
    </div>
  );
}
